import { translate } from "@/i18n/messages.js";
// src/ui/theme.js
// 仅负责 Canvas 视觉绘制，不参与任何游戏规则或状态变化。

const backgroundImage = new Image();
backgroundImage.src = "/assets/beijing.jpg";

export const THEME = Object.freeze({
  bgTop: "#082640",
  bgBottom: "#03111f",
  panel: "rgba(5, 25, 42, 0.88)",
  panelSoft: "rgba(10, 39, 61, 0.74)",
  cyan: "#52f6e8",
  cyanSoft: "#83fff4",
  cyanDark: "#159baf",
  blue: "#4bbcff",
  white: "#ffffff",
  muted: "rgba(255, 255, 255, 0.90)",
  danger: "#ff5d78",
  success: "#5ff6b6",
  locked: "#506477"
});

export function getCanvasSize(ctx) {
  const width = Number(ctx.canvas.dataset.logicalWidth) || ctx.canvas.width;
  const height = Number(ctx.canvas.dataset.logicalHeight) || ctx.canvas.height;
  return { width, height };
}

export function roundedPath(ctx, x, y, width, height, radius = 12) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function drawCoverImage(ctx, image, x, y, width, height) {
  const imageRatio = image.naturalWidth / image.naturalHeight;
  const targetRatio = width / height;
  let sx = 0;
  let sy = 0;
  let sw = image.naturalWidth;
  let sh = image.naturalHeight;

  if (targetRatio > imageRatio) {
    sh = image.naturalWidth / targetRatio;
    sy = (image.naturalHeight - sh) / 2;
  } else {
    sw = image.naturalHeight * targetRatio;
    sx = (image.naturalWidth - sw) / 2;
  }
  ctx.drawImage(image, sx, sy, sw, sh, x, y, width, height);
}

export function drawNeonBackground(ctx, options = {}) {
  const { overlay = 0.22, decorations = true } = options;
  const { width, height } = getCanvasSize(ctx);

  if (backgroundImage.complete && backgroundImage.naturalWidth > 0) {
    drawCoverImage(ctx, backgroundImage, 0, 0, width, height);
  } else {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, THEME.bgTop);
    gradient.addColorStop(1, THEME.bgBottom);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  const shade = ctx.createLinearGradient(0, 0, 0, height);
  shade.addColorStop(0, `rgba(1, 10, 20, ${overlay * 0.45})`);
  shade.addColorStop(0.52, `rgba(0, 8, 17, ${overlay})`);
  shade.addColorStop(1, `rgba(0, 5, 13, ${Math.min(0.72, overlay + 0.18)})`);
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, width, height);

  if (!decorations) return;

  const now = performance.now() / 1000;
  const isTall = height / width > 1.3;
  const motionScale = isTall ? 1.45 : 1;

  // ============================================================
  // 1. 动态十字架装饰线（大幅度，按百分比位置）
  // ============================================================
  ctx.save();
  const linePositions = [0.12, 0.28, 0.44, 0.60, 0.76, 0.92];
  for (let i = 0; i < linePositions.length; i++) {
    const baseY = height * linePositions[i];
    const phase = i * 1.2;

    // 浮动幅度 ±40px
    const floatY = baseY + Math.sin(now * 0.4 + phase) * 40;
    const breath = 0.05 + 0.40 * (0.5 + 0.5 * Math.sin(now * 0.5 + phase * 1.3));
    const lengthScale = 0.3 + 1.7 * (0.5 + 0.5 * Math.sin(now * 0.35 + phase * 0.7));

    ctx.globalAlpha = breath;
    ctx.strokeStyle = "rgba(72, 236, 238, 0.9)";
    ctx.lineWidth = 1.8;
    ctx.shadowColor = "#52f6e8";
    ctx.shadowBlur = breath * 20;

    // 左侧短线
    const leftLen = 70 * lengthScale;
    ctx.beginPath();
    ctx.moveTo(10, floatY);
    ctx.lineTo(10 + leftLen, floatY);
    ctx.stroke();

    // 右侧短线（独立相位）
    const rightPhase = phase + 1.8;
    const rightFloatY = baseY + Math.sin(now * 0.4 + rightPhase) * 40;
    const rightLen = 70 * (0.3 + 1.7 * (0.5 + 0.5 * Math.sin(now * 0.35 + rightPhase * 0.7)));
    const rightBreath = 0.05 + 0.40 * (0.5 + 0.5 * Math.sin(now * 0.5 + rightPhase * 1.3));
    ctx.globalAlpha = rightBreath;
    ctx.shadowBlur = rightBreath * 20;
    ctx.beginPath();
    ctx.moveTo(width - 10, rightFloatY + 40);
    ctx.lineTo(width - 10 - rightLen, rightFloatY + 40);
    ctx.stroke();
  }

  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
  ctx.restore();

  // ============================================================
  // 2. 小白圆点（8字形轨迹，大幅度，均匀分布）
  // ============================================================
  ctx.save();
  const dotPositions = [
    { x: 0.06, y: 0.10, r: 3.5 },
    { x: 0.94, y: 0.16, r: 4.5 },
    { x: 0.10, y: 0.42, r: 3.5 },
    { x: 0.90, y: 0.58, r: 3.5 },
    { x: 0.15, y: 0.78, r: 3.5 },
    { x: 0.85, y: 0.88, r: 4.5 },
    { x: 0.22, y: 0.28, r: 3.0 },
    { x: 0.78, y: 0.35, r: 3.0 },
    { x: 0.08, y: 0.60, r: 3.0 },
    { x: 0.92, y: 0.72, r: 3.0 },
    // 再增加两个中心区域的点，更丰富
    { x: 0.40, y: 0.15, r: 2.5 },
    { x: 0.60, y: 0.85, r: 2.5 }
  ];

  for (const dot of dotPositions) {
    const baseX = width * dot.x;
    const baseY = height * dot.y;
    const phase = dot.x * 5 + dot.y * 3;
    const speed = 0.36 + (dot.x + dot.y) * 0.18;

    // 水平摆动 ±55px，垂直浮动 ±40px，并加上更多轨迹变化
    const hOffset = (Math.cos(now * speed + phase * 1.3) * 52 + Math.sin(now * 0.78 + phase * 0.7) * 18) * motionScale;
    const vOffset = (Math.sin(now * speed * 1.1 + phase * 1.1) * 46 + Math.cos(now * 0.62 + phase * 1.5) * 12) * motionScale;
    const x = baseX + hOffset;
    const y = baseY + vOffset;

    // 呼吸大小 0.15~3.0 倍
    const pulse = 0.15 + 2.85 * (0.5 + 0.5 * Math.sin(now * 0.60 + phase * 1.5));
    const radius = Math.max(1.5, dot.r * pulse);

    const alpha = 0.22 + 0.76 * (0.5 + 0.5 * Math.sin(now * 0.55 + phase * 1.2));
    ctx.globalAlpha = alpha * 0.88;
    ctx.fillStyle = "rgba(200, 255, 255, 0.96)";
    ctx.shadowColor = "#52f6e8";
    ctx.shadowBlur = 14 * pulse + 10;

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  // ============================================================
  // 3. 轨道流动小球（增加明显移动感）
  // ============================================================
  const orbitDots = [
    { x: 0.22, y: 0.18, r: 2.8, orbitX: 38, orbitY: 26, speed: 0.90, phase: 0.2 },
    { x: 0.82, y: 0.22, r: 3.2, orbitX: 44, orbitY: 32, speed: 0.78, phase: 1.4 },
    { x: 0.15, y: 0.72, r: 2.4, orbitX: 26, orbitY: 38, speed: 1.02, phase: 2.1 },
    { x: 0.80, y: 0.80, r: 3.0, orbitX: 34, orbitY: 28, speed: 0.85, phase: 3.3 }
  ];

  for (const orb of orbitDots) {
    const centerX = width * orb.x;
    const centerY = height * orb.y;
    const theta = now * orb.speed + orb.phase;
    const x = centerX + (Math.cos(theta) * orb.orbitX + Math.sin(theta * 1.4) * 8) * motionScale;
    const y = centerY + (Math.sin(theta) * orb.orbitY + Math.cos(theta * 1.2) * 6) * motionScale;
    const radius = orb.r + Math.sin(now * 1.6 + orb.phase) * 0.7 * motionScale;
    const alpha = 0.28 + 0.62 * (0.5 + 0.5 * Math.sin(now * 1.3 + orb.phase * 0.9));

    ctx.globalAlpha = alpha;
    ctx.fillStyle = "rgba(180, 248, 255, 0.95)";
    ctx.shadowColor = "rgba(82,246,232,0.95)";
    ctx.shadowBlur = 16;

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
  ctx.restore();
}

export function drawGlassPanel(ctx, rect, options = {}) {
  const {
    radius = 18,
    fill = THEME.panel,
    stroke = "rgba(82, 246, 232, 0.58)",
    glow = 12,
    lineWidth = 1.5
  } = options;

  ctx.save();
  roundedPath(ctx, rect.x, rect.y, rect.width, rect.height, radius);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = stroke;
  ctx.shadowColor = stroke;
  ctx.shadowBlur = glow;
  ctx.stroke();
  ctx.restore();
}

export function drawPillButton(ctx, rect, label, options = {}) {
  const {
    active = false,
    disabled = false,
    fontSize = 22,
    compact = false,
    accent = THEME.cyan,
    textColor = disabled ? "#708797" : THEME.white
  } = options;

  ctx.save();
  const radius = rect.height / 2;
  roundedPath(ctx, rect.x, rect.y, rect.width, rect.height, radius);
  const gradient = ctx.createLinearGradient(rect.x, rect.y, rect.x, rect.y + rect.height);
  if (disabled) {
    gradient.addColorStop(0, "rgba(51, 68, 83, 0.84)");
    gradient.addColorStop(1, "rgba(25, 36, 49, 0.9)");
  } else if (active) {
    gradient.addColorStop(0, "rgba(32, 120, 139, 0.96)");
    gradient.addColorStop(1, "rgba(6, 48, 70, 0.98)");
  } else {
    gradient.addColorStop(0, "rgba(18, 57, 82, 0.94)");
    gradient.addColorStop(1, "rgba(5, 29, 48, 0.98)");
  }
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.lineWidth = active ? 2.2 : 1.5;
  ctx.strokeStyle = disabled ? "rgba(110, 135, 151, 0.45)" : accent;
  ctx.shadowColor = disabled ? "transparent" : accent;
  ctx.shadowBlur = disabled ? 0 : active ? 14 : 8;
  ctx.stroke();

  if (!disabled && rect.height >= 36) {
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "rgba(255,255,255,0.50)";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(rect.x + radius * 0.58, rect.y + radius * 0.45);
    ctx.lineTo(rect.x + radius * 0.92, rect.y + radius * 0.25);
    ctx.stroke();
  }

  ctx.shadowColor = disabled ? "transparent" : "rgba(255, 255, 255, 0.42)";
  ctx.shadowBlur = disabled ? 0 : 2;
  ctx.fillStyle = textColor;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `800 ${fontSize}px "Arial Black", "Microsoft YaHei", sans-serif`;
  ctx.fillText(label, rect.x + rect.width / 2, rect.y + rect.height / 2 + (compact ? 0 : 1));
  ctx.restore();
}

export function drawTitle(ctx, lines, centerX, startY, options = {}) {
  const {
    fontSize = 54,
    lineHeight = fontSize * 0.86,
    pureWhite = false,
    fontFamily = '"Arial Black", "Microsoft YaHei", sans-serif'
  } = options;

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 ${fontSize}px ${fontFamily}`;
  ctx.lineJoin = "round";

  for (let i = 0; i < lines.length; i += 1) {
    const y = startY + i * lineHeight;

    if (pureWhite) {
      ctx.lineWidth = Math.max(2, fontSize * 0.045);
      ctx.strokeStyle = "rgba(0, 10, 22, 0.92)";
      ctx.shadowColor = "rgba(0, 0, 0, 0.45)";
      ctx.shadowBlur = 8;
      ctx.strokeText(lines[i], centerX, y + 2);
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#ffffff";
      ctx.fillText(lines[i], centerX, y);
      continue;
    }

    ctx.lineWidth = Math.max(4, fontSize * 0.09);
    ctx.strokeStyle = "rgba(1, 28, 42, 0.95)";
    ctx.strokeText(lines[i], centerX + 2, y + 4);
    ctx.lineWidth = Math.max(2, fontSize * 0.04);
    ctx.strokeStyle = THEME.cyan;
    ctx.shadowColor = THEME.cyan;
    ctx.shadowBlur = 9;
    ctx.strokeText(lines[i], centerX, y);
    ctx.shadowBlur = 0;
    ctx.fillStyle = THEME.white;
    ctx.fillText(lines[i], centerX, y);
  }
  ctx.restore();
}

// TOP_BAR_FLAT_STYLE_V3_TEXT_START

// TOP_BAR_FLAT_STYLE_V3_TEXT_END

export function drawTopBar(ctx, title, options = {}) {
  const { width } = getCanvasSize(ctx);
  const {
    backRect = null,
    backLabel = null,
    rightLabel = null,
    rightRect = null,
    language = null
  } = options;

  const docLang =
    typeof document !== "undefined"
      ? document.documentElement?.lang
      : null;

  const resolvedBackLabel =
    backLabel
    || `← ${translate(
      language || docLang || "en",
      "back"
    )}`;
  const titleY = 42;

  ctx.save();
  ctx.fillStyle = THEME.white;
  ctx.shadowColor = "rgba(255, 255, 255, 0.34)";
  ctx.shadowBlur = 2;
  ctx.textBaseline = "middle";

  if (backRect) {
    backRect.x = 4;
    backRect.y = 18;
    backRect.width = 100;
    backRect.height = 48;

    ctx.textAlign = "left";
    ctx.font = '800 15px "Microsoft YaHei UI", "Microsoft YaHei", "Segoe UI", sans-serif';
    ctx.fillText(resolvedBackLabel, 10, titleY);
  }

  ctx.textAlign = "center";
  ctx.font = '900 22px "Arial Black", "Microsoft YaHei UI", "Microsoft YaHei", sans-serif';
  ctx.fillText(title, width / 2, titleY);

  if (rightRect && rightLabel) {
    rightRect.x = width - 100;
    rightRect.y = 18;
    rightRect.width = 96;
    rightRect.height = 48;

    ctx.textAlign = "right";
    ctx.font = '800 14px "Microsoft YaHei UI", "Microsoft YaHei", "Segoe UI", sans-serif';
    ctx.fillText(rightLabel, width - 10, titleY);
  }

  ctx.restore();
}

export function drawSmallText(ctx, text, x, y, options = {}) {
  const { color = THEME.muted, size = 12, align = "center", weight = 500 } = options;
  ctx.save();
  ctx.fillStyle = color;
  ctx.shadowColor = "rgba(255, 255, 255, 0.30)";
  ctx.shadowBlur = size >= 11 ? 1.6 : 1;
  ctx.font = `${weight} ${size}px "Microsoft YaHei UI", "Microsoft YaHei", "Segoe UI", sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, y);
  ctx.restore();
}